// grupa artykułów blog (Wise UI) - `/home/blog/articles/:sectionSymbol`, karty WiseBlogCard

import React, { FC, useLayoutEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { Slide, Slider } from 'pure-react-carousel';
import { Trans } from 'react-i18next';

import { Link } from 'components/controls';
import { useRWD } from 'hooks';
import { useGetHomeBlogArticles } from 'api';
import WiseBlogCard from 'components/wise/WiseBlogCard/WiseBlogCard';
import WiseCarousel from 'pages/wise/WiseProduct/components/WiseCarousel';

import styles from 'theme/components/wise/WiseHomeArticlesGroup/WiseHomeArticlesGroup.module.scss';

const ARTICLE_CARD_W = 500;
const ARTICLE_CARD_H = 250;
const MAX_VISIBLE_SLIDES = 3;
const SLIDE_GAP_PX = 8;
const BLOG_SECTION_SYMBOL = 'TUTORIAL';

function countCardsThatFitRow(widthPx: number): number {
  if (widthPx <= 0) {
    return 1;
  }
  const slot = ARTICLE_CARD_W + SLIDE_GAP_PX;
  return Math.max(1, Math.floor((widthPx + SLIDE_GAP_PX) / slot));
}

const WiseHomeArticlesGroup: FC<{ articleGroupId: string }> = (_props) => {
  const { isMobile } = useRWD();
  const contentRef = useRef<HTMLDivElement>(null);
  const [fitsInRow, setFitsInRow] = useState<number | null>(null);

  const { data: blogData, isLoading, isFetching } = useGetHomeBlogArticles(BLOG_SECTION_SYMBOL);

  const articles = blogData?.items ?? [];
  const articleCount = articles.length;
  const loadingEmpty = (isLoading || isFetching) && articleCount === 0;

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const measure = () => {
      setFitsInRow(countCardsThatFitRow(el.getBoundingClientRect().width));
    };

    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [articleCount]);

  const visibleSlidesInCarousel = useMemo(() => {
    if (articleCount < 1) {
      return 1;
    }
    if (isMobile) {
      return 1;
    }
    return Math.min(
      MAX_VISIBLE_SLIDES,
      articleCount,
      Math.max(1, fitsInRow ?? MAX_VISIBLE_SLIDES)
    );
  }, [articleCount, isMobile, fitsInRow]);

  const skeletonSlideCount = useMemo(() => {
    if (isMobile) {
      return 1;
    }
    return Math.min(MAX_VISIBLE_SLIDES, Math.max(1, fitsInRow ?? MAX_VISIBLE_SLIDES));
  }, [isMobile, fitsInRow]);

  const viewAllPath = useMemo(() => {
    const raw = articles[0]?.url_to_all_articles;
    if (!raw) {
      return '/knowledge-base';
    }
    try {
      const parsed = new URL(raw);
      return `${parsed.pathname || '/knowledge-base'}${parsed.search}`;
    } catch {
      return '/knowledge-base';
    }
  }, [articles]);

  const headerAction =
    articleCount > 0 ? (
      <Link className={styles.viewAllCta} to={viewAllPath}>
        <Trans>Zobacz wszystkie</Trans>
      </Link>
    ) : undefined;

  const showNavArrows = !isMobile;

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Components-containers-WiseHomeArticlesGroup'
      )}>
      <div ref={contentRef} className={styles.content}>
        {loadingEmpty && (
          <div className={styles.loadingSkeleton} aria-busy>
            {Array.from({ length: skeletonSlideCount }).map((_, index) => (
              <div key={index} className={styles.loadingCard} />
            ))}
          </div>
        )}
        {articleCount > 0 && (
          <WiseCarousel
            className={styles.carousel}
            totalSlides={articleCount}
            visibleSlides={visibleSlidesInCarousel}
            naturalSlideWidth={ARTICLE_CARD_W}
            naturalSlideHeight={ARTICLE_CARD_H}
            relaxHeight
            title={<Trans>Baza wiedzy</Trans>}
            headerAction={headerAction}
            showNavArrows={showNavArrows}
            disableKeyboard
            disableAnimation={isFetching}>
            <Slider>
              {articles.map((article, index) => (
                <Slide key={article.id} index={index} className={styles.carouselSlide}>
                  <div className={styles.slideInner}>
                    <WiseBlogCard
                      title={article.title}
                      backgroundPicture={article.background_picture}
                      to={`/blog/${article.url_key || article.id}`}
                    />
                  </div>
                </Slide>
              ))}
            </Slider>
          </WiseCarousel>
        )}
      </div>
    </div>
  );
};

export default WiseHomeArticlesGroup;
